import type { ApiLogger } from '@klareospace/pharos-lib-types';
import { TELEMETRY_NETWORK_ERROR_STATUS } from '../constants';

/**
 * Create a console-based API logger.
 * In development mode, logs requests and responses at the debug level.
 * Errors are always logged regardless of mode.
 *
 * @param isDev - Whether the app is running in development mode (controls debug-level logging)
 */
export function createApiLogger(isDev: boolean): ApiLogger {
  return {
    onError: (method, url, status, durationMs, error): void => {
      console.error(
        `[API] ${method.toUpperCase()} ${url} -> ${status ?? TELEMETRY_NETWORK_ERROR_STATUS} (${Math.round(durationMs)}ms)`,
        error,
      );
    },
    onRequest: (method, url): void => {
      if (isDev) console.debug(`[API] ${method.toUpperCase()} ${url}`);
    },
    onResponse: (method, url, status, durationMs): void => {
      if (isDev) console.debug(`[API] ${method.toUpperCase()} ${url} -> ${status} (${Math.round(durationMs)}ms)`);
    },
  };
}

/**
 * Compose multiple ApiLogger instances into a single one.
 * Each hook fans out to every provided logger that implements it.
 * Individual logger calls are wrapped in a silent try/catch — a throwing
 * logger never prevents the others from running and never propagates to the caller.
 *
 * @param loggers - One or more ApiLogger instances to compose
 */
export function composeApiLoggers(...loggers: ApiLogger[]): ApiLogger {
  return {
    onError: (method, url, status, durationMs, error): void => {
      for (const logger of loggers) {
        if (logger.onError) {
          try {
            logger.onError(method, url, status, durationMs, error);
          } catch {
            // intentionally swallowed — telemetry must never cause an app error
          }
        }
      }
    },
    onRequest: (method, url): void => {
      for (const logger of loggers) {
        if (logger.onRequest) {
          try {
            logger.onRequest(method, url);
          } catch {
            // intentionally swallowed — telemetry must never cause an app error
          }
        }
      }
    },
    onResponse: (method, url, status, durationMs): void => {
      for (const logger of loggers) {
        if (logger.onResponse) {
          try {
            logger.onResponse(method, url, status, durationMs);
          } catch {
            // intentionally swallowed — telemetry must never cause an app error
          }
        }
      }
    },
  };
}
